'use client';

import { useFormContext, useWatch } from 'react-hook-form';
import { useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { MAX_FILE_SIZE, isAllowedDocumentFile } from './multi-file-validation';
import {
  type MultiFileDocumentTypeOption,
  type MultiFileItem,
} from './multi-file-types';
import { MultiFileContent } from './multi-file-content';
import { useObjectUrlLifecycle } from './multi-file-object-url';
import type { HistoricFileReference } from '@/lib/camunda/form-file-values';
import { CamundaHistoryService } from '@/lib/camunda/history-service';
import { normalizeMultiFileItems } from './multi-file-item-schema';

interface MultiFileFieldProps {
  name: string;
  label: string;
  required?: boolean;
  requiredDocumentType?: string;
  documentTypeOptions?: readonly MultiFileDocumentTypeOption[];
  minItems?: number;
  disabled?: boolean;
  className?: string;
  allowDuplicateTypes?: boolean;
  addButtonLabel?: string;
  emptyTitle?: string;
  emptyHint?: string;
  replaceButtonTitle?: string;
  createItem?: (file: File, items: Array<{ file?: File | null; tipo?: string }>) => MultiFileItem;
  onReplaceFile?: (params: {
    file: File;
    index: number;
    items: MultiFileItem[];
  }) => MultiFileItem[];
  canRemoveItem?: (item: MultiFileItem, index: number) => boolean;
  canChangeType?: (item: MultiFileItem) => boolean;
  renderAdditionalHeaders?: () => ReactNode;
  renderAdditionalCells?: (params: {
    item: MultiFileItem;
    index: number;
    disabled: boolean;
  }) => ReactNode;
}

const DOCUMENT_TYPE_OPTIONS = [
  { value: 'Propuesta', label: 'Propuesta' },
  { value: 'Operaciones', label: 'Operaciones' },
] as const;

async function downloadHistoricFile(
  value: HistoricFileReference,
  replaceObjectUrl: (blob: Blob, consumerId?: string) => string,
): Promise<void> {
  const blob = await CamundaHistoryService.downloadParticipationHistoricFile(value);
  const url = replaceObjectUrl(blob, value.historicDetailId);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = value.filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

export function MultiFileField({
  name,
  label,
  required = false,
  requiredDocumentType,
  minItems = 2,
  disabled = false,
  className,
  documentTypeOptions = DOCUMENT_TYPE_OPTIONS,
  allowDuplicateTypes = false,
  addButtonLabel = 'Agregar archivo',
  emptyTitle = 'Haz clic para seleccionar archivos',
  emptyHint = 'PDF, Word o Excel — máx. 5MB por archivo',
  replaceButtonTitle = 'Reemplazar archivo',
  createItem,
  onReplaceFile,
  canRemoveItem,
  canChangeType,
  renderAdditionalHeaders,
  renderAdditionalCells,
}: MultiFileFieldProps) {
  const form = useFormContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const replaceIndexRef = useRef<number | null>(null);
  const [historicDownloadErrors, setHistoricDownloadErrors] = useState<Record<number, string>>({});
  const [historicDownloads, setHistoricDownloads] = useState<Set<number>>(() => new Set());
  const { replaceObjectUrl } = useObjectUrlLifecycle();

  const rawItems = useWatch({ control: form.control, name });
  const items: MultiFileItem[] = Array.isArray(rawItems)
    ? normalizeMultiFileItems(rawItems)
    : [];
  const hasItems = items.length > 0;
  const usedTypes = new Set<string>();
  for (const item of items) {
    if (item.tipo) usedTypes.add(item.tipo);
  }
  const hasRequiredDocumentType = items.some(
    item => item.tipo === requiredDocumentType && item.file instanceof File,
  );
  const hasFieldError = requiredDocumentType
    ? required && form.formState.isSubmitted && !hasRequiredDocumentType
    : required && form.formState.isSubmitted && items.length < minItems;

  const handleFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles?.length) return;

    const newItems: MultiFileItem[] = [];
    for (let i = 0; i < selectedFiles.length; i += 1) {
      const file = selectedFiles[i];
      if (!file || !isAllowedDocumentFile(file) || file.size > MAX_FILE_SIZE) continue;
      newItems.push(
        createItem ? createItem(file, [...items, ...newItems]) : { file, tipo: '' },
      );
    }

    if (newItems.length > 0) {
      form.setValue(name, [...items, ...newItems], { shouldValidate: true });
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleTypeChange = (index: number, tipo: string) => {
    form.setValue(
      name,
      items.map((item, itemIndex) => itemIndex === index ? { ...item, tipo } : item),
      { shouldValidate: true },
    );
  };

  const handleRemove = (index: number) => {
    form.setValue(
      name,
      items.filter((_, itemIndex) => itemIndex !== index),
      { shouldValidate: true },
    );
  };

  const handleReplace = (index: number) => {
    replaceIndexRef.current = index;
    replaceInputRef.current?.click();
  };

  const handleReplaceFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const index = replaceIndexRef.current;
    if (!file || index === null || !isAllowedDocumentFile(file) || file.size > MAX_FILE_SIZE) return;

    const updated = onReplaceFile
      ? onReplaceFile({ file, index, items })
      : items.map((item, itemIndex) => itemIndex === index ? { ...item, file } : item);
    form.setValue(name, updated, { shouldValidate: true });
    replaceIndexRef.current = null;
    if (replaceInputRef.current) replaceInputRef.current.value = '';
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleHistoricDownload = async (index: number, historicFile: HistoricFileReference) => {
    setHistoricDownloads(current => new Set(current).add(index));
    setHistoricDownloadErrors(current => {
      const next = { ...current };
      delete next[index];
      return next;
    });
    try {
      await downloadHistoricFile(historicFile, replaceObjectUrl);
    } catch (error) {
      setHistoricDownloadErrors(current => ({
        ...current,
        [index]: error instanceof Error ? error.message : 'No se pudo descargar el archivo histórico',
      }));
    }
    setHistoricDownloads(current => {
      const next = new Set(current);
      next.delete(index);
      return next;
    });
  };

  return (
    <MultiFileContent
      form={form}
      name={name}
      label={label}
      required={required}
      disabled={disabled}
      canChangeType={canChangeType}
      className={className}
      items={items}
      hasItems={hasItems}
      hasFieldError={hasFieldError}
      usedTypes={usedTypes}
      documentTypeOptions={documentTypeOptions}
      allowDuplicateTypes={allowDuplicateTypes}
      addButtonLabel={addButtonLabel}
      emptyTitle={emptyTitle}
      emptyHint={emptyHint}
      replaceButtonTitle={replaceButtonTitle}
      fileInputRef={fileInputRef}
      replaceInputRef={replaceInputRef}
      historicDownloadErrors={historicDownloadErrors}
      historicDownloads={historicDownloads}
      onFilesSelected={handleFilesSelected}
      onReplaceFileSelected={handleReplaceFileSelected}
      onTypeChange={handleTypeChange}
      onRemove={handleRemove}
      onReplace={handleReplace}
      onTriggerFileInput={triggerFileInput}
      onHistoricDownload={handleHistoricDownload}
      canRemoveItem={canRemoveItem}
      renderAdditionalHeaders={renderAdditionalHeaders}
      renderAdditionalCells={renderAdditionalCells}
    />
  );
}
